import { supabase, Stock, MarketIndex, Watchlist, Portfolio } from './supabase';

export const marketService = {
  async getMarketIndices() {
    const { data, error } = await supabase
      .from('market_indices')
      .select('*')
      .order('code', { ascending: true });

    if (error) throw error;
    return data as MarketIndex[];
  },

  async getStocks(filters?: {
    sector?: string;
    minPrice?: number;
    maxPrice?: number;
    minPE?: number;
    maxPE?: number;
  }) {
    let query = supabase.from('stocks').select('*');

    if (filters?.sector) {
      query = query.eq('sector', filters.sector);
    }
    if (filters?.minPrice) {
      query = query.gte('price', filters.minPrice);
    }
    if (filters?.maxPrice) {
      query = query.lte('price', filters.maxPrice);
    }
    if (filters?.minPE) {
      query = query.gte('pe_ratio', filters.minPE);
    }
    if (filters?.maxPE) {
      query = query.lte('pe_ratio', filters.maxPE);
    }

    const { data, error } = await query.order('market_cap', { ascending: false });

    if (error) throw error;
    return data as Stock[];
  },

  async getStockByCode(code: string) {
    const { data, error } = await supabase
      .from('stocks')
      .select('*')
      .eq('code', code)
      .maybeSingle();

    if (error) throw error;
    return data as Stock | null;
  },

  async getWatchlist(userId: string) {
    const { data, error } = await supabase
      .from('watchlists')
      .select('*')
      .eq('user_id', userId);

    if (error) throw error;
    return data as Watchlist[];
  },

  async addToWatchlist(userId: string, stockCode: string) {
    const { data, error } = await supabase
      .from('watchlists')
      .insert({ user_id: userId, stock_code: stockCode })
      .select()
      .single();

    if (error) throw error;
    return data as Watchlist;
  },

  async removeFromWatchlist(userId: string, stockCode: string) {
    const { error } = await supabase
      .from('watchlists')
      .delete()
      .eq('user_id', userId)
      .eq('stock_code', stockCode);

    if (error) throw error;
  },

  async getPortfolio(userId: string) {
    const { data, error } = await supabase
      .from('portfolios')
      .select('*')
      .eq('user_id', userId);

    if (error) throw error;
    return data as Portfolio[];
  },

  async addToPortfolio(userId: string, stockCode: string, quantity: number, avgPrice: number) {
    const existing = await supabase
      .from('portfolios')
      .select('*')
      .eq('user_id', userId)
      .eq('stock_code', stockCode)
      .maybeSingle();

    if (existing.data) {
      const totalQuantity = existing.data.quantity + quantity;
      const newAvgPrice = ((existing.data.quantity * existing.data.avg_price) + (quantity * avgPrice)) / totalQuantity;

      const { data, error } = await supabase
        .from('portfolios')
        .update({
          quantity: totalQuantity,
          avg_price: newAvgPrice,
          updated_at: new Date().toISOString()
        })
        .eq('id', existing.data.id)
        .select()
        .single();

      if (error) throw error;
      return data as Portfolio;
    } else {
      const { data, error } = await supabase
        .from('portfolios')
        .insert({
          user_id: userId,
          stock_code: stockCode,
          quantity,
          avg_price: avgPrice
        })
        .select()
        .single();

      if (error) throw error;
      return data as Portfolio;
    }
  },

  async removeFromPortfolio(portfolioId: string) {
    const { error } = await supabase
      .from('portfolios')
      .delete()
      .eq('id', portfolioId);

    if (error) throw error;
  },

  calculateChange(current: number, previous: number) {
    return current - previous;
  },

  calculateChangePercent(current: number, previous: number) {
    return ((current - previous) / previous) * 100;
  },

  formatPrice(price: number) {
    return new Intl.NumberFormat('id-ID').format(price);
  },

  formatLargeNumber(num: number) {
    if (num >= 1_000_000_000_000) {
      return `${(num / 1_000_000_000_000).toFixed(2)}T`;
    }
    if (num >= 1_000_000_000) {
      return `${(num / 1_000_000_000).toFixed(2)}M`;
    }
    if (num >= 1_000_000) {
      return `${(num / 1_000_000).toFixed(2)}Jt`;
    }
    return num.toString();
  }
};
